<template>
  <div class="composite-page">
    <header class="composite-header">
      <button type="button" class="btn-text" title="Retour à l'accueil" @click="goHome">
        Accueil
      </button>
      <button type="button" class="btn-text" title="Retour" @click="goBack">
        Retour
      </button>
      <div class="composite-title">
        <h1>{{ bloc?.object_name ?? 'Composite' }}</h1>
        <span v-if="bloc?.inventory_number" class="composite-inventory">
          {{ bloc.inventory_number }}
        </span>
      </div>
      <div class="zoom-controls">
        <template v-if="viewerReady">
          <button type="button" class="btn-icon" title="Zoom avant" @click="zoomBy(1.3)">
            <span class="material-symbols-outlined">zoom_in</span>
          </button>
          <button type="button" class="btn-icon" title="Zoom arrière" @click="zoomBy(1 / 1.3)">
            <span class="material-symbols-outlined">zoom_out</span>
          </button>
          <button type="button" class="btn-icon" title="Recadrer" @click="goHomeView">
            <span class="material-symbols-outlined">fit_screen</span>
          </button>
        </template>
        <a
          v-if="hdUrl"
          class="btn-icon"
          :href="hdUrl"
          :download="`${resolvedSlug}.png`"
          title="Télécharger l'image HD"
        >
          <span class="material-symbols-outlined">download</span>
        </a>
      </div>
    </header>

    <div v-if="loading" class="composite-state">Chargement...</div>
    <div v-else-if="error" class="composite-state error">{{ error }}</div>
    <div v-show="!loading && !error" ref="osdHost" class="osd-host"></div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import OpenSeadragon from 'openseadragon';
import { blocsApi, type Bloc } from '@api/blocs.api';
import { decodeTransportBytes, getApiBase } from '@api/client';

const route = useRoute();
const router = useRouter();

const osdHost = ref<HTMLElement | null>(null);
const bloc = ref<Bloc | null>(null);
const loading = ref(true);
const error = ref('');
const viewerReady = ref(false);
const resolvedSlug = ref<string | null>(null);
const hdUrl = ref<string | null>(null);

let viewer: OpenSeadragon.Viewer | null = null;

function compositeSlugCandidates(loaded: Bloc): string[] {
  const out: string[] = [];
  const add = (value?: string | null) => {
    const trimmed = value?.trim();
    if (trimmed && !out.includes(trimmed)) out.push(trimmed);
  };
  add(loaded.composite_slug);
  add(loaded.object_name);
  add(loaded.inventory_number);
  return out;
}

async function resolveExistingSlug(candidates: string[]): Promise<string | null> {
  const base = getApiBase();
  for (const slug of candidates) {
    try {
      const res = await fetch(`${base}/images/composites/${encodeURIComponent(slug)}.dzi`, {
        method: 'HEAD',
      });
      if (res.ok) return slug;
    } catch {
      // essayer le candidat suivant
    }
  }
  return candidates[0] ?? null;
}

function goHome() {
  router.push('/');
}

function goBack() {
  if (window.history.state?.back != null) {
    router.back();
    return;
  }
  router.push({ path: '/', query: { tab: 'blocs' } });
}

function zoomBy(factor: number) {
  viewer?.viewport.zoomBy(factor);
  viewer?.viewport.applyConstraints();
}

function goHomeView() {
  viewer?.viewport.goHome();
}

async function loadDziTileSource(slug: string) {
  const base = getApiBase();
  const encoded = encodeURIComponent(slug);
  try {
    const res = await fetch(`${base}/images/composites/${encoded}.dzi`);
    if (!res.ok) return null;
    const bytes = await decodeTransportBytes(new Uint8Array(await res.arrayBuffer()));
    const xml = new TextDecoder().decode(bytes).trim();
    if (!xml.startsWith('<')) return null;
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    if (doc.querySelector('parsererror')) return null;
    const image = doc.querySelector('Image');
    const size = doc.querySelector('Size');
    const width = size?.getAttribute('Width');
    const height = size?.getAttribute('Height');
    if (!image || !width || !height) return null;
    return {
      Image: {
        xmlns: 'http://schemas.microsoft.com/deepzoom/2008',
        Url: `${base}/images/composites/${encoded}_files/`,
        Format: image.getAttribute('Format') || 'jpeg',
        Overlap: image.getAttribute('Overlap') || '1',
        TileSize: image.getAttribute('TileSize') || '256',
        Size: { Width: width, Height: height },
      },
    };
  } catch {
    return null;
  }
}

function destroyViewer() {
  if (viewer) {
    viewer.destroy();
    viewer = null;
  }
  viewerReady.value = false;
}

onMounted(async () => {
  const id = parseInt(String(route.params.id), 10);
  if (Number.isNaN(id)) {
    loading.value = false;
    error.value = 'Identifiant de bloc invalide';
    return;
  }

  try {
    const loaded = await blocsApi.getById(id);
    bloc.value = loaded;
    const slug = await resolveExistingSlug(compositeSlugCandidates(loaded));
    resolvedSlug.value = slug;
    if (slug) {
      const originalUrl = `${getApiBase()}/images/composites/${encodeURIComponent(slug)}/original`;
      try {
        const hdRes = await fetch(originalUrl, { method: 'HEAD' });
        if (hdRes.ok) hdUrl.value = originalUrl;
      } catch {
        hdUrl.value = null;
      }
    }
    if (!slug) {
      loading.value = false;
      error.value = 'Aucune photo composite associée à ce bloc';
      return;
    }

    loading.value = false;
    await nextTick();
    if (!osdHost.value) {
      error.value = "Impossible d'initialiser le viewer";
      return;
    }

    const tileSource = await loadDziTileSource(slug);
    if (!tileSource) {
      error.value =
        'Tuiles composites introuvables. Générer les pyramides DZI (npm run composites:build dans backend).';
      return;
    }
    viewer = OpenSeadragon({
      element: osdHost.value,
      prefixUrl: '',
      showNavigationControl: false,
      showNavigator: false,
      visibilityRatio: 1,
      minZoomImageRatio: 0.8,
      maxZoomPixelRatio: 2,
      animationTime: 0.4,
      crossOriginPolicy: 'Anonymous',
      tileSources: tileSource,
    });

    viewer.addHandler('open', () => {
      viewerReady.value = true;
    });
    viewer.addHandler('open-failed', () => {
      error.value =
        'Tuiles composites introuvables. Générer les pyramides DZI (npm run composites:build dans backend).';
      destroyViewer();
    });
  } catch {
    loading.value = false;
    error.value = 'Bloc introuvable';
  }
});

onBeforeUnmount(() => {
  destroyViewer();
});
</script>

<style scoped>
.composite-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background-color: #1a1a1a;
  color: #e0e0e0;
}

.composite-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background-color: #252525;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
}

.btn-text {
  background: none;
  border: 1px solid #444;
  color: #e0e0e0;
  border-radius: 4px;
  padding: 0.35rem 0.75rem;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-text:hover {
  background-color: #333;
}

.composite-title {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
}

.composite-title h1 {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.composite-inventory {
  color: #999;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.zoom-controls {
  display: flex;
  gap: 0.35rem;
}

.btn-icon {
  background: none;
  border: 1px solid #444;
  color: #e0e0e0;
  border-radius: 4px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.btn-icon:hover {
  background-color: #333;
}

a.btn-icon {
  text-decoration: none;
  color: inherit;
  box-sizing: border-box;
}

.btn-icon .material-symbols-outlined {
  font-size: 22px;
}

.osd-host {
  flex: 1;
  min-height: 0;
  background-color: #111;
}

.composite-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.composite-state.error {
  color: #e88;
}
</style>
